import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import Layout, { Container } from "../components/layout";
import { apiurl } from "../config/config";
import { Save, Pencil, X } from "lucide-react";

const SupplierDetails = () => {
  const { supplierObjId } = useParams();
  const [supplier, setSupplier] = useState(null);
  const [form, setForm] = useState(null);
  const [edit, setEdit] = useState(false);

  const fetchSupplier = useCallback(() => {
    axios
      .get(`${apiurl}/api/supplier/get/${supplierObjId}`)
      .then((res) => {
        setSupplier(res.data.data);
        setForm(res.data.data);
      })
      .catch((err) => {
        console.error("Failed to fetch supplier:", err);
      });
  }, [supplierObjId]);

  useEffect(() => {
    fetchSupplier();
  }, [fetchSupplier]);

  const save = async () => {
    try {
      const { _id, supplierCode, createdAt, updatedAt, ...payload } = form;

      const res = await axios.put(
        `${apiurl}/api/supplier/update/${supplier._id}`,
        payload
      );

      setSupplier(res.data.data);
      setForm(res.data.data);
      setEdit(false);
    } catch (err) {
      console.error("Failed to save supplier:", err);
    }
  };

  const updateItem = (index, key, value) => {
    const items = [...(form.itemsSupplied || [])];
    items[index] = { ...items[index], [key]: value };
    setForm({ ...form, itemsSupplied: items });
  };

  if (!form) return <div className="p-10">Loading...</div>;

  return (
    <Layout>
      <Container overflow="overflow-scroll">
        <div className="max-w-6xl mx-auto space-y-6">

          {/* HEADER */}
          <div className="bg-white rounded-xl p-6 shadow flex justify-between">
            <div>
              <h1 className="text-2xl font-bold">{form.name}</h1>
              <p className="text-sm text-gray-500">
                Supplier Code: {supplier?.supplierCode}
              </p>
            </div>

            <div className="flex gap-2">
              {edit ? (
                <>
                  <Btn icon={<Save size={16} />} text="Save" onClick={save} />
                  <Btn
                    icon={<X size={16} />}
                    text="Cancel"
                    onClick={() => {
                      setForm(supplier);
                      setEdit(false);
                    }}
                  />
                </>
              ) : (
                <Btn
                  icon={<Pencil size={16} />}
                  text="Edit"
                  onClick={() => setEdit(true)}
                />
              )}
            </div>
          </div>

          {/* BASIC DETAILS */}
          <Section title="Basic Details">
            <Input label="Name" value={form.name} edit={edit}
              onChange={(v) => setForm({ ...form, name: v })} />

            <Input label="Contact Person" value={form.contactPerson} edit={edit}
              onChange={(v) => setForm({ ...form, contactPerson: v })} />

            <Input label="Phone" value={form.phone} edit={edit}
              onChange={(v) => setForm({ ...form, phone: v })} />

            <Input label="Alt Phone" value={form.altPhone} edit={edit}
              onChange={(v) => setForm({ ...form, altPhone: v })} />

            <Input label="Email" value={form.email} edit={edit}
              onChange={(v) => setForm({ ...form, email: v })} />

            <Input label="Website" value={form.website} edit={edit}
              onChange={(v) => setForm({ ...form, website: v })} />
          </Section>

          {/* TAX DETAILS */}
          <Section title="Tax Details">
            <Input label="GSTIN" value={form.gstin} edit={edit}
              onChange={(v) => setForm({ ...form, gstin: v })} />

            <Input label="PAN" value={form.pan} edit={edit}
              onChange={(v) => setForm({ ...form, pan: v })} />
          </Section>

          {/* TAGS */}
          <Section title="Supplier Tags">
            <Input
              label="Tags (comma separated)"
              value={form.tags?.join(",")}
              edit={edit}
              onChange={(v) =>
                setForm({
                  ...form,
                  tags: v.split(",").map((t) => t.trim()),
                })
              }
            />
          </Section>

          {/* BANK DETAILS */}
          <Section title="Bank Details">
            <Input label="Bank Name" value={form.bankDetails?.name} edit={edit}
              onChange={(v) =>
                setForm({
                  ...form,
                  bankDetails: { ...form.bankDetails, name: v },
                })
              } />

            <Input label="Account Holder" value={form.bankDetails?.accountHolderName} edit={edit}
              onChange={(v) =>
                setForm({
                  ...form,
                  bankDetails: {
                    ...form.bankDetails,
                    accountHolderName: v,
                  },
                })
              } />

            <Input label="Account Number" value={form.bankDetails?.accountNumber} edit={edit}
              onChange={(v) =>
                setForm({
                  ...form,
                  bankDetails: {
                    ...form.bankDetails,
                    accountNumber: v,
                  },
                })
              } />

            <Input label="IFSC" value={form.bankDetails?.ifsc} edit={edit}
              onChange={(v) =>
                setForm({
                  ...form,
                  bankDetails: { ...form.bankDetails, ifsc: v },
                })
              } />
          </Section>

          {/* ITEMS SUPPLIED */}
          <Section title="Items Supplied">
            {form.itemsSupplied?.map((item, i) => (
              <div key={i} className="border rounded-lg p-4 mb-3">
                <Input label="Item Name" value={item.name} edit={edit}
                  onChange={(v) => updateItem(i, "name", v)} />

                <Input label="Category" value={item.category} edit={edit}
                  onChange={(v) => updateItem(i, "category", v)} />

                <Input label="UOM" value={item.uom} edit={edit}
                  onChange={(v) => updateItem(i, "uom", v)} />
              </div>
            ))}

            {edit && (
              <button
                className="text-indigo-600 text-sm"
                onClick={() =>
                  setForm({
                    ...form,
                    itemsSupplied: [...(form.itemsSupplied || []), {}],
                  })
                }
              >
                + Add Item
              </button>
            )}
          </Section>

          {/* COMMERCIAL */}
          <Section title="Commercial Terms">
            <Input label="Payment Terms" value={form.paymentTerms} edit={edit}
              onChange={(v) => setForm({ ...form, paymentTerms: v })} />

            <Input label="Credit Limit" value={form.creditLimit} edit={edit}
              onChange={(v) => setForm({ ...form, creditLimit: v })} />
          </Section>

          {/* STATUS */}
          <Section title="Status">
            <select
              disabled={!edit}
              value={form.status}
              onChange={(e) =>
                setForm({ ...form, status: e.target.value })
              }
              className="input"
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="suspended">Suspended</option>
              <option value="deleted">Deleted</option>
            </select>
          </Section>

        </div>
      </Container>
    </Layout>
  );
};

export default SupplierDetails;

/* ---------- REUSABLE COMPONENTS ---------- */

const Section = ({ title, children }) => (
  <div className="bg-white rounded-xl shadow p-6">
    <h2 className="font-semibold text-gray-700 mb-4">{title}</h2>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {children}
    </div>
  </div>
);

const Input = ({ label, value, onChange, edit }) => (
  <div>
    <label className="text-xs text-gray-500">{label}</label>
    <input
      value={value || ""}
      disabled={!edit}
      onChange={(e) => onChange(e.target.value)}
      className={`w-full border rounded-lg px-3 py-2 text-sm ${
        edit ? "focus:ring-2 focus:ring-indigo-500" : "bg-gray-100"
      }`}
    />
  </div>
);

const Btn = ({ icon, text, onClick }) => (
  <button
    onClick={onClick}
    className="flex items-center gap-1 bg-indigo-600 text-white px-4 py-2 rounded-lg text-sm"
  >
    {icon} {text}
  </button>
);